import { useQuery, useMutation, useSubscription } from '@apollo/client';
import { useCallback } from 'react';
import {
  GET_DASHBOARD_STATS,
  GET_INVOICES,
  GET_SUSCEPTIBLE_INVOICES,
  GET_DELETED_INVOICES,
  GET_ELECTRONIC_INVOICES,
  GET_INVOICE_TYPE_STATS,
  GET_DELETION_CONFIG,
  GET_CASH_MODE_CONFIG,
  GET_RESOLUTIONS,
  GET_DELETION_LOGS,
  TOGGLE_CASH_MODE,
  UPDATE_DELETION_CONFIG,
  DELETE_INVOICES_MANUALLY,
  EXECUTE_SCHEDULED_DELETION,
  ACTIVATE_RESOLUTION,
  INVOICE_CREATED_SUBSCRIPTION,
  INVOICE_DELETED_SUBSCRIPTION,
  CONFIG_UPDATED_SUBSCRIPTION,
} from '../graphql/operations';
import {
  Invoice,
  DashboardStats,
  DeletionConfig,
  CashModeConfig,
  Resolution,
  DeletionLog,
  InvoiceStatus,
  ResolutionType,
  TimeRange,
  DeleteFrequency,
} from '../types/billing.types';

// Hook for dashboard statistics
export const useDashboardStats = (filters?: {
  startDate?: Date;
  endDate?: Date;
}) => {
  const { data, loading, error, refetch } = useQuery<{ getDashboardStats: DashboardStats }>(
    GET_DASHBOARD_STATS,
    {
      variables: {
        startDate: filters?.startDate?.toISOString(),
        endDate: filters?.endDate?.toISOString(),
      },
      pollInterval: 30000, // Poll every 30 seconds
    }
  );

  return {
    stats: data?.getDashboardStats,
    loading,
    error,
    refetch,
  };
};

// Hook for invoices management
export const useInvoices = (filters?: {
  resolutionType?: ResolutionType;
  status?: InvoiceStatus;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  offset?: number;
}) => {
  const { data, loading, error, refetch } = useQuery<{ getInvoices: Invoice[] }>(
    GET_INVOICES,
    {
      variables: filters,
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    invoices: data?.getInvoices || [],
    loading,
    error,
    refetch,
  };
};

// Hook for susceptible invoices
export const useSusceptibleInvoices = (filters?: {
  startDate?: Date;
  endDate?: Date;
}) => {
  const variables = {
    startDate: filters?.startDate?.toISOString(),
    endDate: filters?.endDate?.toISOString(),
  };

  console.log('📦 useSusceptibleInvoices - variables:', variables);

  const { data, loading, error, refetch } = useQuery<{ getSusceptibleInvoices: Invoice[] }>(
    GET_SUSCEPTIBLE_INVOICES,
    {
      variables,
      fetchPolicy: 'network-only', // Siempre ir al servidor
      notifyOnNetworkStatusChange: true,
    }
  );

  console.log('📦 useSusceptibleInvoices - data:', data?.getSusceptibleInvoices?.length || 0, 'invoices');

  return {
    susceptibleInvoices: data?.getSusceptibleInvoices || [],
    loading,
    error,
    refetch,
  };
};

// Hook for deleted invoices
export const useDeletedInvoices = (filters?: {
  startDate?: Date;
  endDate?: Date;
}) => {
  const { data, loading, error, refetch } = useQuery<{ getDeletedInvoices: Invoice[] }>(
    GET_DELETED_INVOICES,
    {
      variables: {
        startDate: filters?.startDate?.toISOString(),
        endDate: filters?.endDate?.toISOString(),
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    deletedInvoices: data?.getDeletedInvoices || [],
    loading,
    error,
    refetch,
  };
};

// Hook for electronic invoices (FE=1, cannot be modified)
export const useElectronicInvoices = (filters?: {
  startDate?: Date;
  endDate?: Date;
}) => {
  const variables = {
    startDate: filters?.startDate?.toISOString(),
    endDate: filters?.endDate?.toISOString(),
  };

  console.log('⚡ useElectronicInvoices - variables:', variables);

  const { data, loading, error, refetch } = useQuery<{ getElectronicInvoices: Invoice[] }>(
    GET_ELECTRONIC_INVOICES,
    {
      variables,
      fetchPolicy: 'network-only', // Siempre ir al servidor
      notifyOnNetworkStatusChange: true,
    }
  );

  console.log('⚡ useElectronicInvoices - data:', data?.getElectronicInvoices?.length || 0, 'invoices');

  return {
    electronicInvoices: data?.getElectronicInvoices || [],
    loading,
    error,
    refetch,
  };
};

// Hook for invoice type statistics (electronic vs susceptible)
export const useInvoiceTypeStats = (filters?: {
  startDate?: Date;
  endDate?: Date;
}) => {
  const { data, loading, error, refetch } = useQuery<{
    getInvoiceTypeStats: {
      electronic: { count: number; amount: number };
      susceptible: { count: number; amount: number };
    };
  }>(GET_INVOICE_TYPE_STATS, {
    variables: {
      startDate: filters?.startDate?.toISOString(),
      endDate: filters?.endDate?.toISOString(),
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });

  return {
    stats: data?.getInvoiceTypeStats,
    loading,
    error,
    refetch,
  };
};

// Hook for deletion configuration
export const useDeletionConfig = () => {
  const { data, loading, error, refetch } = useQuery<{ getDeletionConfig: DeletionConfig }>(
    GET_DELETION_CONFIG
  );

  const [updateConfigMutation, { loading: updating }] = useMutation(UPDATE_DELETION_CONFIG, {
    refetchQueries: [{ query: GET_DELETION_CONFIG }],
  });

  const updateConfig = useCallback(
    async (config: {
      isActive: boolean;
      timeRange: TimeRange;
      deleteAmount?: number;
      deletePercentage?: number;
      frequency: DeleteFrequency;
    }) => {
      try {
        await updateConfigMutation({
          variables: { config },
        });
      } catch (err) {
        console.error('Error updating deletion config:', err);
        throw err;
      }
    },
    [updateConfigMutation]
  );

  return {
    config: data?.getDeletionConfig,
    loading,
    error,
    updating,
    updateConfig,
    refetch,
  };
};

// Hook for cash mode control
export const useCashMode = () => {
  const { data, loading, error, refetch } = useQuery<{ getCashModeConfig: CashModeConfig }>(
    GET_CASH_MODE_CONFIG
  );

  const [toggleMutation, { loading: toggling }] = useMutation(TOGGLE_CASH_MODE, {
    refetchQueries: [
      { query: GET_CASH_MODE_CONFIG },
      { query: GET_DASHBOARD_STATS },
    ],
  });

  const toggleCashMode = useCallback(
    async (enabled: boolean) => {
      try {
        await toggleMutation({
          variables: { enabled },
        });
      } catch (err) {
        console.error('Error toggling cash mode:', err);
        throw err;
      }
    },
    [toggleMutation]
  );

  return {
    cashModeConfig: data?.getCashModeConfig,
    loading,
    error,
    toggling,
    toggleCashMode,
    refetch,
  };
};

// Hook for resolutions management
export const useResolutions = () => {
  const { data, loading, error, refetch } = useQuery<{ getResolutions: Resolution[] }>(
    GET_RESOLUTIONS
  );

  const [activateMutation, { loading: activating }] = useMutation(ACTIVATE_RESOLUTION, {
    refetchQueries: [{ query: GET_RESOLUTIONS }],
  });

  const activateResolution = useCallback(
    async (resolutionId: string) => {
      try {
        await activateMutation({
          variables: { resolutionId },
        });
      } catch (err) {
        console.error('Error activating resolution:', err);
        throw err;
      }
    },
    [activateMutation]
  );

  return {
    resolutions: data?.getResolutions || [],
    loading,
    error,
    activating,
    activateResolution,
    refetch,
  };
};

// Hook for invoice deletion
export const useInvoiceDeletion = () => {
  const [deleteManuallyMutation, { loading: deleting }] = useMutation(
    DELETE_INVOICES_MANUALLY,
    {
      refetchQueries: [
        { query: GET_DASHBOARD_STATS },
        { query: GET_INVOICES },
        { query: GET_SUSCEPTIBLE_INVOICES },
      ],
    }
  );

  const [executeScheduledMutation, { loading: executing }] = useMutation(
    EXECUTE_SCHEDULED_DELETION,
    {
      refetchQueries: [
        { query: GET_DASHBOARD_STATS },
        { query: GET_INVOICES },
        { query: GET_SUSCEPTIBLE_INVOICES },
      ],
    }
  );

  const deleteInvoicesManually = useCallback(
    async (invoiceIds: string[], reason?: string) => {
      try {
        await deleteManuallyMutation({
          variables: {
            input: { invoiceIds, reason },
          },
        });
      } catch (err) {
        console.error('Error deleting invoices manually:', err);
        throw err;
      }
    },
    [deleteManuallyMutation]
  );

  const executeScheduledDeletion = useCallback(async () => {
    try {
      await executeScheduledMutation();
    } catch (err) {
      console.error('Error executing scheduled deletion:', err);
      throw err;
    }
  }, [executeScheduledMutation]);

  return {
    deleting,
    executing,
    deleteInvoicesManually,
    executeScheduledDeletion,
  };
};

// Hook for deletion logs
export const useDeletionLogs = (filters?: {
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  offset?: number;
}) => {
  const { data, loading, error, refetch } = useQuery<{ getDeletionLogs: DeletionLog[] }>(
    GET_DELETION_LOGS,
    {
      variables: filters,
    }
  );

  return {
    logs: data?.getDeletionLogs || [],
    loading,
    error,
    refetch,
  };
};

// Hook for real-time subscriptions
export const useBillingSubscriptions = () => {
  const { data: invoiceCreated } = useSubscription(INVOICE_CREATED_SUBSCRIPTION);
  const { data: invoiceDeleted } = useSubscription(INVOICE_DELETED_SUBSCRIPTION);
  const { data: configUpdated } = useSubscription(CONFIG_UPDATED_SUBSCRIPTION);

  return {
    invoiceCreated: invoiceCreated?.invoiceCreated,
    invoiceDeleted: invoiceDeleted?.invoiceDeleted,
    configUpdated: configUpdated?.configUpdated,
  };
};
